// Storage-quota upload block — the shared decision layer (ISP-9999).
//
// Gordon, 2026-09-04: "If you are already over your subscription storage limit,
// you should not be able to upload any more media (right now you are not
// blocked)."
//
// This is an ACCOUNT-QUOTA rule (used bytes >= plan limit). It is NOT a
// per-file size cap — three earlier PRs (#5164 Feedback modal, #5173 AI
// builder, #5188 Business Box) each added a 50 MB per-FILE cap to one modal,
// which is a different rule and does not implement this one.
//
// Pure on purpose: no React, no redux, no network. The boundary comparison is
// where a wrong answer matters (block a user who still has room, or let a full
// one keep uploading), so it is unit-tested on its own in storageLimit.test.js.
//
// BOUNDARY: `used >= limit` — the SAME comparison the backend gate uses
// (utils/storage_quota_gate.py::is_over_storage_limit, matching
// subscription.permissions.HasStorageSpace). It is deliberately one notch
// stricter than the DISPLAY flag in utils/storage.js `normaliseUsage`
// (`used > limit`): if the UI used the looser rule, an account sitting exactly
// at 100% would show an enabled Upload button and then eat a 402 from the
// server. The UI must never offer an action the server will refuse.
//
// mkt-0920-dev-soft-cap-warnings: the upload flow only ever told the user
// AFTER they were already blocked (isStorageFull above). isStorageNearLimit
// backs the soft, non-blocking warning shown BEFORE that point — same shared
// nearLimit() rule the created/cloned-wheel caps use, see utils/nearLimit.js.
import { nearLimit } from "./nearLimit";

/** The machine-readable code the backend returns with its 402. */
export const STORAGE_LIMIT_EXCEEDED_CODE = "storage_limit_exceeded";

/** HTTP status the backend answers with when the account is full. */
export const STORAGE_LIMIT_STATUS = 402;

/**
 * Short label for a disabled upload control (tooltip / aria-label / helper
 * text). Says WHY it is disabled and what to do — never a bare "disabled".
 */
export const STORAGE_FULL_DISABLED_LABEL =
  "Out of storage — upgrade your plan to add more media";

/**
 * Is the account out of storage?
 *
 * @param {object} usage the `normaliseUsage` view-model from `useStorageUsage`.
 * @returns {boolean} true only when we have a real positive limit AND
 *   `usedBytes >= limitBytes`. Unknown / still-loading / unlimited / zero-limit
 *   all return false: never block on an answer we do not have.
 */
export const isStorageFull = (usage) => {
  if (!usage) return false;
  const limit = Number(usage.limitBytes);
  const used = Number(usage.usedBytes);
  if (!Number.isFinite(limit) || limit <= 0) return false;
  if (!Number.isFinite(used)) return false;
  return used >= limit;
};

/**
 * The one sentence shown in the toast, wherever an upload is refused.
 *
 * kelly-1005-P1-portrait-video-length-before-upload: Kelly, 2026-10-05 —
 * "people do not know what 500 MB means." Plain words, never a byte count;
 * the Upgrade link (StorageFullToastBody) is the way out, not a number.
 */
export const storageFullMessage = () =>
  "You're out of storage space on your plan. Upgrade your plan to add more media.";

/**
 * Is the account APPROACHING (not yet at) its storage limit?
 *
 * @param {object} usage the `normaliseUsage` view-model from `useStorageUsage`.
 * @returns {boolean} true only in the 80%-and-under-100% band. An uncapped
 *   plan (`limitBytes` 0/falsy — see normaliseUsage) is never "near" anything.
 */
export const isStorageNearLimit = (usage) => {
  if (!usage) return false;
  return nearLimit(usage.usedBytes, usage.limitBytes || null);
};

/** The sentence for the soft near-limit toast — plain words, no byte count
 * (kelly-1005-P1), since this fires BEFORE the account is actually full. */
export const storageNearLimitMessage = () =>
  "You're running low on storage space. Upgrade your plan to add more room before you run out.";

/**
 * Did this request fail because the account is out of storage?
 *
 * Keys off the backend's machine-readable `code`, never the human copy (copy
 * changes; the contract must not). Reads both body positions the backend
 * writes it to: top level for the web client, and inside `data` for older
 * callers of the same envelope.
 *
 * The 402 status alone is also accepted: this app uses 402 for nothing else,
 * and a proxy that strips the body must not turn a refusal into a silent
 * failure.
 */
export const isStorageLimitError = (error) => {
  const response = error && error.response;
  if (!response) return false;
  const body = response.data || {};
  const code = body.code || (body.data && body.data.code);
  if (code === STORAGE_LIMIT_EXCEEDED_CODE) return true;
  return response.status === STORAGE_LIMIT_STATUS;
};

/**
 * Build the toast sentence from a 402 response body, so the message quotes the
 * SERVER's numbers when we have them and the client's cached usage otherwise.
 */
export const storageLimitErrorMessage = (error, fallbackUsage) => {
  const body = (error && error.response && error.response.data) || {};
  if (typeof body.message === "string" && body.message.trim()) {
    return body.message;
  }
  return storageFullMessage(fallbackUsage);
};
